// Dynamisk dansk tekst til øverunden Komma og punktum.

import { makeRng } from '../data/curriculum/generators';
import { cumulativeWordPool } from './cumulativeWordPool';
import type { GenerateRoundOptions, UserProfile } from '../types';

const FALLBACK_WORDS = [
  'kat',
  'bog',
  'ord',
  'dag',
  'mor',
  'far',
  'mand',
  'god',
  'det',
  'er',
  'kan',
  'har',
  'vi',
  'du',
  'en',
  'til',
  'og',
  'på',
];

const DIALOG_NAMES = ['manden', 'pigen', 'drengen', 'moren', 'faren', 'axel'];

function poolForUser(user: UserProfile): string[] {
  const fromProgress = cumulativeWordPool(user);
  if (fromProgress.length >= 12) return fromProgress;
  return [...new Set([...fromProgress, ...FALLBACK_WORDS])];
}

function pickOne<T>(rng: () => number, items: T[]): T {
  return items[Math.floor(rng() * items.length)]!;
}

function pickUnique(rng: () => number, pool: string[], count: number): string[] {
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

function shortWords(pool: string[], maxLen = 8): string[] {
  return pool.filter((w) => w.length > 0 && w.length <= maxLen && !w.includes(' '));
}

function formatDanishList(words: string[]): string {
  if (words.length === 0) return '';
  if (words.length === 1) return words[0]!;
  if (words.length === 2) return `${words[0]} og ${words[1]}`;
  return `${words.slice(0, -1).join(', ')} og ${words[words.length - 1]}`;
}

export function generatePeriodPractice(
  pool: string[],
  rng: () => number,
): string {
  const singles = pickUnique(rng, shortWords(pool), 5 + Math.floor(rng() * 3)).map(
    (w) => `${w}.`,
  );
  const phraseWords = pickUnique(rng, pool, 3 + Math.floor(rng() * 2));
  const phrase =
    phraseWords.length > 0 ? `${phraseWords.join(' ')}.` : '';
  return [...singles, phrase].filter(Boolean).join(' ');
}

export function generateListPractice(pool: string[], rng: () => number): string {
  const count = 4 + Math.floor(rng() * 3);
  const words = pickUnique(rng, shortWords(pool, 12), count);
  return `${formatDanishList(words)}.`;
}

export function generateDialogPractice(pool: string[], rng: () => number): string {
  const namePool = DIALOG_NAMES;

  const lines: string[] = [];
  const lineCount = 2 + Math.floor(rng() * 2);

  for (let i = 0; i < lineCount; i++) {
    const kind = Math.floor(rng() * 4);
    if (kind === 0) {
      lines.push(`hej, sagde ${pickOne(rng, namePool)}.`);
    } else if (kind === 1) {
      const tail = pickUnique(rng, shortWords(pool), 3).join(' ');
      lines.push(`ja, ${tail}.`);
    } else if (kind === 2) {
      const tail = pickUnique(rng, shortWords(pool), 3).join(' ');
      lines.push(`nej, ${tail}.`);
    } else {
      const w = pickOne(rng, shortWords(pool));
      lines.push(`${w}, sagde ${pickOne(rng, namePool)}.`);
    }
  }

  return lines.join(' ');
}

export function generateMixPractice(pool: string[], rng: () => number): string {
  const parts: string[] = [];

  const micro = pickUnique(rng, shortWords(pool, 6), 2).map((w) => `${w}.`);
  if (micro.length > 0) parts.push(micro.join(' '));

  parts.push(generateDialogPractice(pool, rng));

  const extraSingles = pickUnique(rng, shortWords(pool), 2).map((w) => `${w}.`);
  if (extraSingles.length > 0) parts.push(extraSingles.join(' '));

  parts.push(generateListPractice(pool, rng));

  return parts.filter(Boolean).join(' ');
}

export function punctuationGenerator(
  user: UserProfile,
  kind: 'period' | 'list' | 'dialog' | 'mix',
) {
  const pool = poolForUser(user);
  return (opts: GenerateRoundOptions) => {
    const rng = makeRng(opts.seed);
    switch (kind) {
      case 'period':
        return generatePeriodPractice(pool, rng);
      case 'list':
        return generateListPractice(pool, rng);
      case 'dialog':
        return generateDialogPractice(pool, rng);
      case 'mix':
        return generateMixPractice(pool, rng);
    }
  };
}
